<template>
  <LoadingIndicator
    absolute
    v-if="!teiData"
  />
  <TEIDataError
    v-else-if="teiData.error || optionsCountError"
    :message="teiData.error || optionsCountError"
  />
  <TEILayoutContainer
    v-else
    :asset-id="assetId"
    :show-check-button="showCheckButton"
    :show-checking-button="submittingAttempt"
    :show-reset-button="showCheckButton"
    :show-try-again-button="showTryAgainButton"
    :check-button-enabled="checkButtonEnabled"
    :reset-button-enabled="checkButtonEnabled"
    :scoring-bar="{
      attempts: (showFeedback || updatingAttemptItemTry) ? tries : tries + 1,
      maxAttempts: maxTries,
      score: maxScore,
      showAnswerKey: showAnswerKey,
    }"
    :show-scoring-bar="!showFeedback"
    :show-response-bar="showFeedback"
    :response-bar="{
      state: attemptItemTry.correct ? 'correct' : 'incorrect',
      content: responseBarMessage,
    }"
    :is-displayed="isDisplayed"
    @reset-button-clicked="onResetClicked"
    @check-button-clicked="onCheckClicked"
    @try-again-button-clicked="onTryAgainClicked"
  >
    <div
      class="selected-response-tei"
      :class="`selected-response-tei--${teiLayout}`"
    >
      <TEIInstructionsCard
        v-if="teiData.prompt.lang[teiLang]"
        :position="teiLayoutIsVertical ? 'top' : 'left'"
        class="selected-response-tei__instructions-card"
        :class="{
          'selected-response-tei__instructions-card--left': !teiLayoutIsVertical,
        }"
        :content="promptContent"
        :do-sticker-position="teiLayoutIsVertical ? 'bottom-left' : 'top-right'"
        :image-alt="promptImageAlt"
        :image-url="promptImageUrl"
        :max-lines="promptMaxLines"
      />
      <div class="selected-response-tei__flex-spacer" />
      <ul
        class="selected-response-tei__options"
        :class="`selected-response-tei__options--${formattedOptions.length}-across`"
      >
        <TEISelectedResponseOption
          v-for="option in formattedOptions"
          :key="option.id"
          :asset-id="assetId"
          :disabled="isOptionDisabled"
          :option="option"
          :is-dataset-tei="isDatasetTei"
          :is-multiple-response="isMultipleResponse"
          :layout="optionLayout"
          :max-lines="optionMaxLines(option)"
          @change="onChange"
        />
      </ul>
      <div class="selected-response-tei__flex-spacer" />
    </div>
  </TEILayoutContainer>
</template>

<script>
import { flattenDeep, isNumber } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import TEILayoutContainer from '@/components/teis/TEILayoutContainer.vue';
import TEIDataError from '@/components/teis/TEIDataError.vue';
import TEIInstructionsCard from '@/components/teis/TEIInstructionsCard.vue';
import TEISelectedResponseOption from '@/components/teis/TEISelectedResponseOption.vue';
import {
  TEI_CHECKING_ANSWERS_MODAL_DELAY,
  TEI_INSTRUCTIONS_CARD_ALLOWED_ELEMENTS,
} from '@/lib/constants';
import { extractHtml } from '@/lib/utils';
import fullSlideTEI from '@/mixins/fullSlideTEI';
import modals from '@/mixins/modals';
import {
  useAssessmentStore,
  useEditorStore,
  useModalStore,
  useUserStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

export default {
  name: 'SelectedResponseTEI',
  components: {
    LoadingIndicator,
    TEILayoutContainer,
    TEIDataError,
    TEIInstructionsCard,
    TEISelectedResponseOption,
  },
  mixins: [fullSlideTEI, modals],
  data() {
    return {
      checkingAnswersModalId: 'TEICheckingAnswersModal',
      selectedOptions: [],
      updatingAttemptItemTry: false,
    };
  },
  computed: {
    ...mapState(useUserStore, [
      'userIsStudent',
    ]),
    ...mapState(useEditorStore, [
      'editorViewModePreview',
    ]),
    ...mapState(useAssessmentStore, [
      'attemptIsClearing',
    ]),
    isOptionDisabled() {
      const val = this.submittingAttempt || this.showFeedback
        || this.showAnswerKey || !this.userCanEditAttempt || this.attemptItemTry.correct;

      return !!val;
    },
    checkButtonEnabled() {
      return !!(this.selectedOptions?.length
        && this.userCanEditAttempt
        && !this.submittingAttempt
        && !this.attemptItemTry.correct);
    },
    optionLayout() {
      if (this.teiLayout === 'horizontal-with-list') return 'list';
      if (this.teiLayout === 'vertical-with-list') return 'list-text';
      return 'cards';
    },
    formattedOptions() {
      const options = this.teiData.options || this.teiData.responseFields || [];

      // Scoring options are formatted differently depending on the TEI
      const scoringOptions = this.teiData.scoringData?.scoring || this.teiData.scoring || [];

      const correctAnswerIds = scoringOptions
        .filter((scoringOption) => scoringOption.correct)
        .reduce((acc, scoringOption) => {
          // The "responses" property is an array of correct sets of options
          flattenDeep(scoringOption.responses)
            .forEach((correctResponse) => {
              acc.push(correctResponse.id);
            });
          return acc;
        }, []);

      return options.map((option, i) => {
        const isSelected = this.isSelected(option.id);

        return {
          id: option.id,
          inputChecked: isSelected,
          marker: String.fromCharCode(i + 65), // create A B C from index
          accessibilityText: option.lang[this.teiLang].accessibility,
          resource: option.lang[this.teiLang].resource,
          text: option.lang[this.teiLang].text,
          uiState: {
            selected: isSelected && !this.showFeedback,

            // Correct if: showing answer key
            correct: (this.showAnswerKey && correctAnswerIds.includes(option.id))
              // OR the attempt has been checked and is completely correct
              || (isSelected && this.attemptItemTry.checked && this.attemptItemTry.correct)
              // OR the attempt is checked, not completely correct, but this option is correct
              || (isSelected && this.attemptItemTry.checked && !this.attemptItemTry.correct
                && this.attemptItemTry.correct_answers.includes(option.id)),

            incorrect: isSelected && this.attemptItemTry.checked && !this.attemptItemTry.correct
              && !this.attemptItemTry.correct_answers.includes(option.id),
          },
        };
      });
    },
    hasTriesRemaining() {
      return !this.userIsStudent || !isNumber(this.tries)
        || this.tries < this.maxTries;
    },
    isDatasetTei() {
      return this.teiLayout === 'horizontal-with-cards';
    },
    isMultipleResponse() {
      return this.teiData.uiLayout.responseType === 'multiple';
    },
    maxTries() {
      return this.attempt?.max_tries || 0;
    },
    optionsCountError() {
      const options = this.teiData?.options || this.teiData?.responseFields || [];

      if (options.length > 6) {
        return this.$t('TEIs with more than 6 response options cannot be used in this view.');
      }

      return null;
    },
    promptContent() {
      const prompt = this.teiData?.prompt?.lang?.[this.teiLang]?.text;
      const allowedElements = new Set([
        ...TEI_INSTRUCTIONS_CARD_ALLOWED_ELEMENTS,
        ...['table', 'tbody', 'tr', 'td', 'th'],
      ]);
      return extractHtml(prompt, allowedElements);
    },
    promptImageAlt() {
      return this.teiData.prompt.lang[this.teiLang]?.resource?.[0]?.title;
    },
    promptImageUrl() {
      return this.teiData.prompt.lang[this.teiLang]?.resource?.[0]?.url;
    },
    promptMaxLines() {
      if (this.teiLayoutIsVertical) {
        return 3;
      }

      if (this.promptImageUrl) {
        // Calculate how much space the image is taking up, including padding,
        // and subtract that from the total space (13 lines)
        const promptImageDimensions = this.teiData.prompt.lang[this.teiLang]
          ?.resource?.[0]?.dimensions;

        // Default to 8 since that's the amount of lines left if we had
        // the largest possible image
        if (!promptImageDimensions?.height) return 8;

        const promptImageHeight = Math.min(
          (promptImageDimensions.height / promptImageDimensions.width) * 200,
          130, // the image is capped at 130px in css
        );
        const lineHeight = 31;
        const imageHeightInLines = Math.ceil((promptImageHeight + 16) / lineHeight);

        return 13 - imageHeightInLines;
      }

      return 13;
    },
    responseBarMessage() {
      // check multilang feedback first
      if (
        this.attemptItemTry?.multilanguage_feedback
        && this.teiLang
        && this.attemptItemTry?.multilanguage_feedback[this.teiLang]
      ) {
        return this.attemptItemTry?.multilanguage_feedback[this.teiLang];
      }

      // no multilang? try regular feedback
      if (this.attemptItemTry?.try_feedback) {
        return this.attemptItemTry?.try_feedback;
      }

      return this.hasTriesRemaining
        ? this.$t('Don’t worry! You will have another chance at this question.')
        : this.$t('Hm...Not quite. Check with your teacher if you have questions.');
    },
    showAnswerKey() {
      return this.mode === 'EDIT';
    },
    showCheckButton() {
      return !this.showFeedback && !this.submittingAttempt;
    },
    showTryAgainButton() {
      return this.showFeedback && !this.attemptItemTry.correct
        && this.hasTriesRemaining && !this.submittingAttempt;
    },
    optionsHaveImage() {
      return this.formattedOptions.every((option) => option.resource?.url);
    },
    teiLayoutIsVertical() {
      return ['vertical', 'vertical-with-list'].includes(this.teiLayout);
    },
    teiLayout() {
      // Note: There is an additional layout called 'horizontal-with-list' that
      // is not currently being used.

      const optionCount = this.formattedOptions?.length;
      if ([5, 6].includes(optionCount) && !this.optionsHaveImage) {
        return 'vertical-with-list';
      }

      const prompt = this.teiData.prompt.lang[this.teiLang]?.text || '';

      // Prompts including tables display side-by-side with options as cards
      // when the number of options is less than 5.
      if (prompt.toLowerCase().includes('<table') && optionCount < 5) {
        return 'horizontal-with-cards';
      }

      // All other formats use the standard layout with cards below the prompt
      return 'vertical';
    },
  },
  async created() {
    this.selectedOptions = this.attemptItemTry.response || [];

    // If we've already submitted once before, show the feedback immediately
    this.showFeedback = this.attemptItemTry.checked;
  },
  methods: {
    ...mapActions(useModalStore, [
      types.OPEN_MODAL,
    ]),
    ...mapActions(useAssessmentStore, [
      types.SUBMIT_RESPONSE,
      types.UPDATE_UNSUBMITTED_ATTEMPT,
      types.SET_ATTEMPT_CLEARING,
    ]),
    isSelected(optionId) {
      return optionId === this.selectedOptions || this.selectedOptions.includes(optionId);
    },
    onChange(e) {
      const optId = e.target.value;

      if (!this.isMultipleResponse) {
        this.selectedOptions = optId;
      } else if (this.selectedOptions.includes(optId)) {
        this.selectedOptions = this.selectedOptions.filter((opt) => opt !== optId);
      } else {
        this.selectedOptions.push(optId);
      }

      // When the user's answer changes, flag the attempt as dirty
      // so they get prompted before navigating to another page (and
      // losing their unsubmitted attempt)
      this[types.UPDATE_UNSUBMITTED_ATTEMPT](true);
    },
    onCheckClicked() {
      this.checkAnswersModal = this.openModalDeferred(TEI_CHECKING_ANSWERS_MODAL_DELAY, {
        id: this.checkingAnswersModalId,
        type: this.checkingAnswersModalId,
      });
      this.submittingAttempt = true;
      this[types.SUBMIT_RESPONSE]({
        teiId: this.assetId,
        payload: this.selectedOptions,
      });
    },
    onResetClicked() {
      this.selectedOptions = [];
    },
    onTryAgainClicked() {
      this.selectedOptions = [];
      this.showFeedback = false;
    },
    optionMaxLines(option) {
      const optionCount = this.formattedOptions?.length || 0;
      if (optionCount === 6 && option.resource?.url) {
        return 1;
      }
      if (optionCount === 5 && option.resource?.url) {
        return 2;
      }
      return undefined;
    },
  },
  watch: {
    attemptItemTry(newTry) {
      // if we've just cleared answers
      if (this.attemptIsClearing && !newTry.checked && this.isDisplayed) {
        this.submittingAttempt = false;
        this.selectedOptions = newTry.response || [];
        this.showFeedback = newTry.checked;

        // set attempt clearing to false
        this[types.SET_ATTEMPT_CLEARING](false);
      }

      // This watcher is triggered when a response comes back from the api
      if (this.checkAnswersModal) {
        // Make sure the try number doesn't flicker between renders
        this.updatingAttemptItemTry = true;

        this.checkAnswersModal.close(() => {
          this[types.UPDATE_UNSUBMITTED_ATTEMPT](false);
          this.submittingAttempt = false;
          this.selectedOptions = newTry.response || [];
          this.showFeedback = newTry.checked;
          this.updatingAttemptItemTry = false;
        });
      }
    },
  },
};
</script>

<style lang="stylus">
@import './styles/tei-fonts';
$check-button-overhang-height = 34px;

.selected-response-tei {
  color: $nebula-color-platform-neutral-900;
  display: flex;
  font-family: 'Mikado Chan', 'Proxima Vara', Arial, sans-serif;
  font-feature-settings: 'ss02' 0;
  height: 100%;

  &--vertical,
  &--vertical-with-list {
    flex-direction: column;
  }

  &__instructions-card {
    margin: 0 $nebula-space-4x $nebula-space-2x;

    &--left {
      flex-shrink: 0;
      height: auto;
      margin: 20px 0;
      width: 380px;
    }
  }

  &__options {
    display: flex;
    flex-shrink: 1;
    flex-wrap: nowrap;
    gap: 20px;
    justify-content: center;
    margin: 0;
    // Vertically center options between prompt and top of the "Check" button
    margin-bottom: $check-button-overhang-height + $nebula-space-2x;
    min-height: 0;
    padding: 0 $nebula-space-3x;

    &--2-across,
    &--3-across {
      gap: 44px;
    }

    &--5-across,
    &--6-across {
      gap: 12px;
    }

    .selected-response-tei--horizontal-with-cards & {
      align-items: center;
      flex-wrap: wrap;
      gap: 20px;
      margin-bottom: $check-button-overhang-height + $nebula-space-1x;
      margin-top: 20px;
    }

    .selected-response-tei--horizontal-with-list & {
      flex-direction: column;
      gap: 12px;
      margin-top: $nebula-space-2x;
      width: 100%;
    }

    .selected-response-tei--vertical-with-list & {
      background: $nebula-color-white;
      border-radius: $nebula-border-radius-default;
      flex-direction: column;
      gap: $nebula-space-half;
      margin: 6px 30px auto 30px;
      padding: $nebula-space-half;

      &.selected-response-tei__options--5-across {
        margin-top: 28px;
      }
    }
  }

  &__flex-spacer {
    flex-grow: 1;

    .selected-response-tei--vertical-with-list & {
      flex-grow: 0;
    }
  }
}
</style>
